import { ActivityType } from '@bioliberty/core';
import { Profile, SessionSummary } from '@bioliberty/lifemind';
import { ColumnDef, PaginationState, Row } from '@tanstack/react-table';
import { Download, Sparkles } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useAuthentication } from '../../../auth';
import { Button } from '../../../components/Button';
import { DataTable } from '../../../components/DataTable';
import { Tile } from '../../../components/Tile';
import { getFeatureFlag } from '../../../stores/slices/feature-flags';
import { hasAdminAccess } from '../../../util/auth';
import { formatDate, formatSecondsString } from '../../../util/date';
import { ExportPatientSessionsModal } from './ExportPatientSessionsModal';
import { SessionActivityTable } from './SessionActivityTable';
import { ViewSessionNoteModal } from './ViewSessionNoteModal';

interface SessionsActivityTileProps {
  patientId: number;
  sessions: SessionSummary[];
  dateRange?: [Date, Date] | null;
  page?: number;
  pageSize: number;
  pageCount: number;
  onPaginationChange: (state: PaginationState) => void;
}

export const SessionsActivityTile = ({
  patientId,
  sessions,
  dateRange,
  page,
  pageSize,
  pageCount,
  onPaginationChange
}: SessionsActivityTileProps) => {
  const { isEnabled: isComputerVisionEnabled } = useSelector(getFeatureFlag('computerVision'));
  const currentUser = useAuthentication();
  const [showExportModal, setShowExportModal] = useState(false);
  const [sessionIdToEdit, setSessionIdToEdit] = useState<string>();
  const sessionToEdit = sessions.find((s) => s.sessionId === sessionIdToEdit);
  const sessionsColumns: ColumnDef<SessionSummary>[] = getSessionsColumns({
    currentUser,
    setSessionIdToEdit
  });

  const rows = useMemo(() => {
    return sessions.map((session) => ({
      ...session,
      activities: session.activities.filter(
        ({ activityTypeId }) => activityTypeId !== ActivityType.CV_MODE || isComputerVisionEnabled
      )
    }));
  }, [sessions, isComputerVisionEnabled]);

  return (
    <>
      <Tile
        title="Sessions"
        actions={
          !!sessions.length && (
            <Button
              size="m"
              iconSize="m"
              icon={Download}
              onClick={() => setShowExportModal(true)}
              data-testid="export-sessions-button"
            >
              Export Data
            </Button>
          )
        }
        className="flex max-h-full min-w-full"
      >
        <DataTable
          rows={rows}
          columns={sessionsColumns}
          page={page}
          pageSize={pageSize}
          pageCount={pageCount}
          onPaginationChange={onPaginationChange}
          renderSubComponent={renderSessionActivities}
          expandable
          stickyHeader
          className="mt-4 min-w-[1000px]"
          testId="patient-sessions-table"
        />
      </Tile>
      <ExportPatientSessionsModal
        patientId={patientId}
        initialDateRange={dateRange}
        open={showExportModal}
        onOpenChange={setShowExportModal}
      />
      {sessionToEdit && (
        <ViewSessionNoteModal
          session={sessionToEdit}
          open
          onOpenChange={(open) => {
            if (!open) {
              setSessionIdToEdit(undefined);
            }
          }}
        />
      )}
    </>
  );
};

const renderSessionActivities = (row: Row<SessionSummary>) => {
  const activities = row.original.activities;
  return <SessionActivityTable activities={activities} />;
};

const getSessionsColumns = ({
  currentUser,
  setSessionIdToEdit
}: {
  currentUser: Profile;
  setSessionIdToEdit: (sessionId: string) => void;
}): ColumnDef<SessionSummary>[] => {
  const columns: ColumnDef<SessionSummary>[] = [
    {
      accessorKey: 'sessionNumber',
      header: '',
      cell: ({ row }) => {
        return (
          <span className="cursor-pointer select-none" onClick={() => row.toggleExpanded()}>
            Session {row.getValue('sessionNumber')}
          </span>
        );
      }
    },
    {
      accessorKey: 'startedOn',
      header: 'Date',
      cell: ({ row }) => formatDate(new Date(row.getValue('startedOn')))
    },
    ...(hasAdminAccess(currentUser)
      ? [
          {
            accessorKey: 'clinicianName',
            header: 'Clinician'
          }
        ]
      : []),
    {
      accessorKey: 'duration',
      header: 'Duration',
      cell: ({ row }) => formatSecondsString(row.getValue('duration'))
    },
    {
      accessorKey: 'activityCount',
      header: 'Total activities',
      cell: ({ row }) => row.original.activities.length
    },
    {
      accessorKey: 'totalTimeTaken',
      header: 'Total time on task',
      cell: ({ row }) => formatSecondsString(row.getValue('totalTimeTaken'))
    },
    {
      accessorKey: 'totalReps',
      header: 'Total reps'
    },
    {
      accessorKey: 'avgFeedbackScore',
      header: 'Average RPE',
      cell: ({ row }) => {
        const value: number | null = row.getValue('avgFeedbackScore');
        if (value === null) {
          return '';
        }
        return Number.isInteger(value) ? value.toString() : value.toFixed(2);
      }
    },
    {
      accessorKey: 'actions',
      header: '',
      cell: ({ row }) => {
        if (!row.original.note) {
          return null;
        }

        return (
          <Button variant="transparent" size="sm" onClick={() => setSessionIdToEdit(row.original.sessionId)}>
            <span className="to-accent flex items-center gap-1 bg-linear-to-r from-green-500 bg-clip-text font-bold text-transparent">
              AI Summary <Sparkles className="text-accent h-4 w-4" />
            </span>
          </Button>
        );
      }
    }
  ];

  return columns;
};
